import isNil from 'lodash/isNil';
import {DisplayAdVo} from '../../common/shared/api/gateway/DisplayAdVo.interface';
import {MetaInfoTypeVo} from '../../common/shared/api/gateway/enums/MetaInfoTypeVo.enum';
import {LayoutVo} from '../../common/shared/api/karina/vo/LayoutVo.interface';
import {MoreVo} from '../../common/shared/api/karina/vo/MoreVo.interface';
import {BaseSectionVo} from '../../common/shared/api/karina/vo/section/BaseSectionVo.interface';
import {TabVo} from '../../common/shared/api/karina/vo/TabVo.interface';
import {ThemeKeywordVo} from '../../common/shared/api/karina/vo/ThemeKeywordVo.interface';
import {ViewDataVo} from '../../common/shared/api/karina/vo/ViewDataVo.interface';
import {
  CardSection,
  Maybe,
  MetaInfoTypeSection,
  RecommendSection,
  SchemeSection,
  Section,
  SectionType,
  TabSection,
  ThemeKeywordSection,
  User,
} from '../generated/types';
import {createGroupWithDisplayAdVo} from './DisplayAdGroup';
import {createGroupWithViewDataVo} from './Group';
import {createMetaInfoType} from './MetaInfoType';
import {createSectionSubTabListAndGroups} from './SectionSubTabListAndGroups';
import {createSectionType} from './SectionType';

export function createSectionWithSectionVo(
  userInfo: Maybe<User>,
  {
    sectionVo,
    layoutVo,
    layoutId,
  }: {
    sectionVo: BaseSectionVo;
    layoutVo: LayoutVo;
    layoutId: string;
  }
): Section {
  const {type, title} = sectionVo;
  const {reference, selected_tab_list} = layoutVo;

  const sectionId = `${layoutId}-section-${sectionVo.uid}`;
  const section: Section = {
    id: sectionId,
    uid: sectionVo.uid,
    type: createSectionType(type),
    title,
    groups: [],
  };

  const sectionType = section.type;

  if ('scheme' in sectionVo) {
    const schemeSection = section as SchemeSection;
    const {scheme} = sectionVo as BaseSectionVo & {
      scheme: string;
    };
    schemeSection.scheme = scheme;
  }

  if ('tab_list' in sectionVo) {
    const {uid, tab_list} = sectionVo as BaseSectionVo & {
      tab_list: TabVo[];
    };
    const selectedTabVo = selected_tab_list?.find(
      selected_tab => selected_tab.uid === uid
    );
    const tabSection = section as TabSection;
    tabSection.sectionMainTabList = [];
    tab_list.forEach(tabVo => {
      const {tab_uid, title, tab_list: section_tab_list, view_data} = tabVo;
      if (!tabSection.sectionMainTabList) {
        tabSection.sectionMainTabList = [];
      }
      const {sectionSubTabList, groups} = createSectionSubTabListAndGroups(
        userInfo,
        {
          view_data,
          section_tab_list,
          reference,
          more: tabVo.more,
          sectionType,
          selectedSubTabUid: selectedTabVo?.value[tab_uid],
          sectionId,
        }
      );
      tabSection.sectionMainTabList.push({
        uid: tab_uid,
        title,
        isSelected: selectedTabVo?.selected_tab_uid === tab_uid,
        subTabList: sectionSubTabList,
        scheme: tabVo.scheme,
        additionalString: tabVo.additional_string,
      });
      section.groups = section.groups.concat(groups);
    });
  }

  if ('theme_keyword_list' in sectionVo) {
    const {theme_keyword_list} = sectionVo as BaseSectionVo & {
      theme_keyword_list: ThemeKeywordVo[];
    };
    (section as ThemeKeywordSection).themeKeywordList = (
      theme_keyword_list || []
    ).map(({uid, title, scheme}) => ({
      uid,
      title: `#${title}`, // 클라에서 # 을 붙여주기로 함
      scheme,
    }));
  }

  // 헬릭스 큐레이션 여부
  if ('is_fetch_recommend' in sectionVo) {
    const {is_fetch_recommend} = sectionVo as BaseSectionVo & {
      is_fetch_recommend?: boolean;
    };
    if (!isNil(is_fetch_recommend)) {
      const recommendSection = section as RecommendSection;

      recommendSection.isRecommendArea = true;
      recommendSection.isRecommendedItems = is_fetch_recommend;
    }
  }

  if ('banner_data' in sectionVo) {
    const {banner_data, is_fetch_recommend} = sectionVo as BaseSectionVo & {
      banner_data: ViewDataVo;
      is_fetch_recommend?: boolean;
    };
    section.groups.push(
      createGroupWithViewDataVo(userInfo, {
        view_data: banner_data,
        sectionId,
        reference,
        sectionType,
        is_fetch_recommend,
      })
    );
  }

  if ('display_ad' in sectionVo) {
    const {display_ad} = sectionVo as BaseSectionVo & {
      display_ad: DisplayAdVo;
    };
    section.groups.push(createGroupWithDisplayAdVo(display_ad, sectionId));
  }

  if ('strategy_data' in sectionVo) {
    const {strategy_data} = sectionVo as BaseSectionVo & {
      strategy_data: ViewDataVo;
    };
    section.groups.push(
      createGroupWithViewDataVo(userInfo, {
        view_data: strategy_data,
        sectionId,
        reference,
        sectionType,
      })
    );
  }

  const metaInfoType = createMetaInfoType(
    section.type,
    (
      sectionVo as BaseSectionVo & {
        meta_info_type: MetaInfoTypeVo;
      }
    ).meta_info_type
  );

  if ('view_data' in sectionVo) {
    const {view_data, more, is_fetch_recommend} = sectionVo as BaseSectionVo & {
      view_data: ViewDataVo;
      more: MoreVo;
      is_fetch_recommend?: boolean;
    };
    section.groups.push(
      createGroupWithViewDataVo(userInfo, {
        view_data,
        sectionId,
        reference,
        sectionType,
        metaInfoType,
        more,
        is_fetch_recommend,
      })
    );
  }

  if (section.type === SectionType.HelixTheme) {
    const {is_fetch_recommend} = sectionVo as BaseSectionVo & {
      banner_data: ViewDataVo;
      is_fetch_recommend?: boolean;
    };
    const cardSection = section as CardSection;

    cardSection.isRecommendArea = true;
    cardSection.isRecommendedItems = is_fetch_recommend;
  }

  if (
    section.type === SectionType.ScrollTheme ||
    section.type === SectionType.Theme ||
    section.type === SectionType.RecentViewed ||
    section.type === SectionType.HelixTheme
  ) {
    (section as MetaInfoTypeSection).metaInfoType = metaInfoType;
  }

  return section;
}
